import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Shield, ChevronLeft, ChevronRight, Check, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { publicApi } from '@/lib/api';
import TenantFooter from '@/components/TenantFooter';
import { usePageTitle } from '@/hooks/usePageTitle';
import { WhistlelinkLanding } from './reportForm/WhistlelinkLanding';
import { WhistleLanding } from './reportForm/WhistleLanding';
import { SuccessScreen } from './reportForm/SuccessScreen';
import { Step1Identification } from './reportForm/Step1Identification';
import { Step2Details } from './reportForm/Step2Details';
import { Step3Confirmation } from './reportForm/Step3Confirmation';

const ReportFormPage = () => {
  const { tenantSlug } = useParams();
  const navigate = useNavigate();

  const [tenant, setTenant] = useState(null);
  const [branding, setBranding] = useState(null);
  usePageTitle(tenant?.name);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  const [showPin, setShowPin] = useState(false);
  const [copied, setCopied] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [layoutView, setLayoutView] = useState('landing'); // only used when layout_style has an institutional landing (whistlelink_style, whistle)

  const [formData, setFormData] = useState({
    is_anonymous: true,
    reporter_name: '',
    reporter_email: '',
    reporter_phone: '',
    notification_email: '',
    category: '',
    description: '',
    incident_date: '',
    location: '',
    department: '',
    persons_involved: '',
    previously_reported: false,
    previous_report_details: '',
    reported_externally: false,
    external_report_details: '',
    good_faith_declaration: false
  });

  useEffect(() => {
    const fetchTenant = async () => {
      try {
        const [tenantRes, brandingRes] = await Promise.all([
          publicApi.getTenant(tenantSlug),
          publicApi.getTenantBranding(tenantSlug)
        ]);
        setTenant(tenantRes.data);
        setBranding(brandingRes.data);
      } catch (err) {
        setError('Entidade não encontrada');
      } finally {
        setLoading(false);
      }
    };
    fetchTenant();
  }, [tenantSlug]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateStep = (currentStep) => {
    switch (currentStep) {
      case 1:
        if (!formData.is_anonymous && !formData.reporter_name) {
          setError('Por favor, indique o seu nome');
          return false;
        }
        break;
      case 2:
        if (!formData.category) {
          setError('Por favor, selecione uma categoria');
          return false;
        }
        if (!formData.description || formData.description.length < 50) {
          setError('Por favor, forneça uma descrição detalhada (mínimo 50 caracteres)');
          return false;
        }
        break;
      case 3:
        if (!formData.good_faith_declaration) {
          setError('Deve aceitar a declaração de boa-fé para prosseguir');
          return false;
        }
        break;
      default:
        break;
    }
    setError('');
    return true;
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    setError('');
    setStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) return;

    setSubmitting(true);
    setError('');

    try {
      // Clean up form data - remove empty strings and convert to proper format
      const cleanedData = { ...formData };

      // Remove empty optional fields
      if (!cleanedData.incident_date) delete cleanedData.incident_date;
      if (!cleanedData.location) delete cleanedData.location;
      if (!cleanedData.department) delete cleanedData.department;
      if (!cleanedData.persons_involved) delete cleanedData.persons_involved;
      if (!cleanedData.previous_report_details) delete cleanedData.previous_report_details;
      if (!cleanedData.external_report_details) delete cleanedData.external_report_details;
      if (!cleanedData.notification_email) delete cleanedData.notification_email;
      if (!cleanedData.reporter_name) delete cleanedData.reporter_name;
      if (!cleanedData.reporter_email) delete cleanedData.reporter_email;
      if (!cleanedData.reporter_phone) delete cleanedData.reporter_phone;

      const response = await publicApi.submitReport(tenantSlug, cleanedData);
      const { tracking_code, pin } = response.data;

      // Upload files if any
      if (files.length > 0) {
        setUploadingFiles(true);
        for (const file of files) {
          try {
            await publicApi.uploadReporterAttachment(tracking_code, pin, file);
          } catch (uploadErr) {
            console.error('Failed to upload file:', file.name, uploadErr);
          }
        }
        setUploadingFiles(false);
      }

      setSuccess(response.data);
    } catch (err) {
      // Handle validation errors properly
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        // FastAPI validation error returns array
        const messages = detail.map(e => e.msg || e.message || String(e)).join(', ');
        setError(messages || 'Erro de validação');
      } else if (typeof detail === 'string') {
        setError(detail);
      } else {
        setError('Erro ao submeter denúncia. Por favor, tente novamente.');
      }
    } finally {
      setSubmitting(false);
      setUploadingFiles(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse text-slate-400">A carregar...</div>
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Entidade não encontrada</h2>
            <p className="text-slate-600 mb-4">O canal de denúncias solicitado não existe.</p>
            <Button onClick={() => navigate('/')}>Voltar ao início</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success Screen
  if (success) {
    return (
      <SuccessScreen
        success={success}
        showPin={showPin}
        setShowPin={setShowPin}
        copied={copied}
        copyToClipboard={copyToClipboard}
        navigate={navigate}
      />
    );
  }

  // Institutional landing screen (opt-in per tenant). Classic tenants (incl.
  // demo, which is always 'classic') skip straight to the form below, unchanged.
  if (branding?.layout_style === 'whistlelink_style' && layoutView === 'landing') {
    return <WhistlelinkLanding tenant={tenant} branding={branding} onStart={() => setLayoutView('form')} />;
  }

  if (branding?.layout_style === 'whistle' && layoutView === 'landing') {
    return <WhistleLanding tenant={tenant} branding={branding} onStart={() => setLayoutView('form')} />;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to={`/${tenant.slug}`} className="flex items-center gap-2" data-testid="tenant-home-link">
              <Shield className="w-6 h-6 text-slate-900" />
              <span className="font-semibold text-slate-900">{tenant.name}</span>
            </Link>
            <Link to="/seguimento" className="text-sm text-slate-600 hover:text-slate-900">
              Acompanhar Denúncia
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Banner */}
      {branding && (
        <div
          className="relative py-12 px-4"
          style={{
            backgroundColor: branding.primary_color || '#0F172A',
            backgroundImage: branding.hero_image_url
              ? `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.6)), url(${process.env.REACT_APP_BACKEND_URL}${branding.hero_image_url})`
              : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
          data-testid="hero-banner"
        >
          <div className="max-w-2xl mx-auto text-center text-white">
            <h1 className="text-2xl md:text-3xl font-bold mb-2" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              {branding.hero_title || 'Canal de Denúncias Interno'}
            </h1>
            <p className="text-sm md:text-base opacity-90">
              {branding.hero_subtitle || 'Submeta a sua denúncia de forma segura e confidencial'}
            </p>
          </div>
        </div>
      )}

      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          {[1, 2, 3].map((s) => (
            <React.Fragment key={s}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-all
                ${step === s ? 'bg-slate-900 text-white' : ''}
                ${step > s ? 'bg-emerald-500 text-white' : ''}
                ${step < s ? 'bg-slate-200 text-slate-500' : ''}
              `}>
                {step > s ? <Check className="w-5 h-5" /> : s}
              </div>
              {s < 3 && (
                <div className={`w-16 h-1 mx-2 rounded ${step > s ? 'bg-emerald-500' : 'bg-slate-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        <Card>
          <CardHeader>
            <CardTitle>
              {step === 1 && 'Identificação'}
              {step === 2 && 'Detalhes da Denúncia'}
              {step === 3 && 'Confirmação'}
            </CardTitle>
            <CardDescription>
              {step === 1 && 'Escolha se pretende identificar-se ou permanecer anónimo'}
              {step === 2 && 'Descreva os factos que pretende denunciar'}
              {step === 3 && 'Reveja e confirme os dados antes de submeter'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert className="mb-6 bg-red-50 border-red-200">
                <AlertCircle className="w-4 h-4 text-red-600" />
                <AlertDescription className="text-red-700">{error}</AlertDescription>
              </Alert>
            )}

            {step === 1 && (
              <Step1Identification formData={formData} handleChange={handleChange} />
            )}

            {step === 2 && (
              <Step2Details
                formData={formData}
                handleChange={handleChange}
                tenant={tenant}
                files={files}
                setFiles={setFiles}
                setError={setError}
              />
            )}

            {step === 3 && (
              <Step3Confirmation formData={formData} handleChange={handleChange} files={files} />
            )}

            {/* Navigation */}
            <div className="flex justify-between mt-8 pt-6 border-t">
              {step > 1 ? (
                <Button variant="outline" onClick={prevStep}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Anterior
                </Button>
              ) : (
                <div />
              )}

              {step < 3 ? (
                <Button onClick={nextStep} data-testid="next-step-btn">
                  Seguinte
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button onClick={handleSubmit} disabled={submitting || uploadingFiles} data-testid="submit-report-btn">
                  {submitting ? (uploadingFiles ? 'A enviar ficheiros...' : 'A submeter...') : 'Submeter Denúncia'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
      <TenantFooter branding={branding} />
    </div>
  );
};

export default ReportFormPage;
